import { tileFromTenhouCode } from "../tiles";
import type { ParseResult } from "../types";

// Converts a Tenhou JSON log (the format embedded in tenhou.net/6/#json=... URLs)
// into mjai events. Each round is a flat array with per-player draw/discard
// arrays in lockstep; calls, kans and riichi are encoded as strings inside
// those arrays, so the round must be replayed turn by turn to recover event
// order. Reference: specs/TENHOU_JSON.md.

const t = tileFromTenhouCode;

/** Strip the tenhou.net/6/#json=...&ts=0 wrapper / percent-encoding, if present. */
export function extractTenhouJson(text: string): string {
    const trimmed = text.trim();
    const urlLine = trimmed.split("\n").find((l) => l.includes("#json="));
    if (urlLine) {
        const start = urlLine.indexOf("#json=") + "#json=".length;
        const encoded = (urlLine.slice(start).split("&")[0] as string).trim();
        return decodeURIComponent(encoded);
    }
    if (/^%7B/i.test(trimmed)) {
        return decodeURIComponent(trimmed);
    }
    return trimmed;
}

type Slot = number | string;

interface TenhouLog {
    name: string[];
    log: unknown[][];
}

/** Tile kind for matching a meld's called tile against a discard (reds ≡ plain 5s). */
function kindOf(code: number): number {
    if (code === 51) return 15;
    if (code === 52) return 25;
    if (code === 53) return 35;
    return code;
}

/** Split a meld string into its letter, letter position (in tile codes), and codes. */
function splitMeld(s: string): { letter: string; pos: number; codes: number[] } {
    const m = /[a-z]/.exec(s);
    if (!m) throw new Error(`malformed meld string: ${s}`);
    const letter = m[0] as string;
    const pos = m.index / 2;
    const codes = s.replace(letter, "").match(/\d{2}/g)?.map(Number);
    if (!codes) throw new Error(`malformed meld string: ${s}`);
    return { letter, pos, codes };
}

/** Seat the meld was called from, given the caller seat and letter position. */
function meldSource(caller: number, pos: number, np: number): number {
    // Letter position among the tile codes: 0 = kamicha, 1 = toimen, >=2 = shimocha.
    const offsets = np === 3 ? [2, 1, 1] : [3, 2, 1];
    return (caller + (offsets[Math.min(pos, 2)] as number)) % np;
}

const RYUKYOKU_REASONS: Record<string, string> = {
    流局: "exhaustive_draw",
    全員聴牌: "exhaustive_draw",
    全員不聴: "exhaustive_draw",
    九種九牌: "kyushu_kyuhai",
    三家和: "sanchahou",
    三家和了: "sanchahou",
    四風連打: "suufon_renda",
    四開槓: "suukaikan",
    四家立直: "suucha_riichi",
    流し満貫: "nagashi_mangan",
};

export function parseTenhou(text: string): ParseResult {
    let raw: unknown;
    try {
        raw = JSON.parse(extractTenhouJson(text));
    } catch (e) {
        throw new Error(`tenhou JSON: ${(e as Error).message}`);
    }
    const data = raw as Partial<TenhouLog>;
    if (!Array.isArray(data.log) || !Array.isArray(data.name)) {
        throw new Error('tenhou JSON: missing "log" or "name"');
    }
    const np = data.name.length;
    const events: MjaiEvent[] = [{ type: "start_game", names: data.name }];
    const warnings: string[] = [];

    for (let k = 0; k < data.log.length; k++) {
        try {
            convertRound(data.log[k] as unknown[], np, events, warnings);
        } catch (e) {
            throw new Error(`tenhou JSON round ${k + 1}: ${(e as Error).message}`);
        }
    }
    events.push({ type: "end_game" });
    return { events, warnings };
}

function convertRound(round: unknown[], np: number, events: MjaiEvent[], warnings: string[]): void {
    if (round.length !== 5 + 3 * np) {
        throw new Error(`expected ${5 + 3 * np} slots, got ${round.length}`);
    }
    const [info, startScores, doraInds, uraInds] = round as [
        number[],
        number[],
        number[],
        number[],
    ];
    const hands: number[][] = [];
    const draws: Slot[][] = [];
    const discards: Slot[][] = [];
    for (let p = 0; p < np; p++) {
        hands.push(round[4 + 3 * p] as number[]);
        draws.push(round[5 + 3 * p] as Slot[]);
        discards.push(round[6 + 3 * p] as Slot[]);
    }
    const result = round[4 + 3 * np] as Slot[] | undefined;

    const roundId = info[0] as number;
    const oya = roundId % 4;
    events.push({
        type: "start_kyoku",
        bakaze: "ESWN"[roundId >> 2],
        kyoku: (roundId % 4) + 1,
        honba: info[1],
        kyotaku: info[2],
        oya,
        dora_marker: t(doraInds[0] as number),
        scores: [...startScores],
        tehais: hands.map((h) => h.map(t)),
    });

    const idx = new Array(np).fill(0) as number[];
    const lastDrawn = new Array(np).fill(null) as (number | null)[];
    const riichi = new Array(np).fill(false) as boolean[];
    const running = [...startScores];
    let cur = oya;
    let prevDiscarder = -1;
    let lastCallable: number | null = null; // last discarded (or kakan'd) tile code
    let doraPtr = 1;
    let pendingReach: number | null = null; // actor whose riichi has not been accepted yet

    const flushReach = () => {
        if (pendingReach !== null) {
            events.push({ type: "reach_accepted", actor: pendingReach });
            running[pendingReach] = (running[pendingReach] as number) - 1000;
            pendingReach = null;
        }
    };
    const revealDora = () => {
        if (doraPtr < doraInds.length) {
            events.push({ type: "dora", dora_marker: t(doraInds[doraPtr] as number) });
            doraPtr++;
        }
    };
    const emitCall = (meldStr: string, actor: number) => {
        const { letter, pos, codes } = splitMeld(meldStr);
        const target = meldSource(actor, pos, np);
        if (target !== prevDiscarder) {
            warnings.push(
                `meld ${meldStr}: letter position points at seat ${target}, using actual discarder ${prevDiscarder}`,
            );
        }
        if (letter === "c") {
            const [called, ...rest] = codes;
            events.push({
                type: "chi",
                actor,
                target: prevDiscarder,
                pai: t(called as number),
                consumed: rest.map(t),
            });
        } else {
            // pon or daiminkan: the code adjacent to the letter is the called tile
            const calledIdx = Math.min(pos, codes.length - 1);
            const called = codes[calledIdx] as number;
            const consumed = codes.filter((_, i) => i !== calledIdx).map(t);
            events.push({
                type: letter === "p" ? "pon" : "daiminkan",
                actor,
                target: prevDiscarder,
                pai: t(called),
                consumed,
            });
        }
    };

    while ((idx[cur] as number) < (draws[cur] as Slot[]).length) {
        const turn = idx[cur] as number;
        const d = (draws[cur] as Slot[])[turn] as Slot;
        flushReach();
        if (typeof d === "string") {
            emitCall(d, cur);
        } else {
            events.push({ type: "tsumo", actor: cur, pai: t(d) });
            lastDrawn[cur] = d;
        }

        if (turn >= (discards[cur] as Slot[]).length) {
            // Tsumo win or an abortive declaration: the turn has no discard slot.
            idx[cur] = turn + 1;
            break;
        }
        const x = (discards[cur] as Slot[])[turn] as Slot;
        idx[cur] = turn + 1;

        if (x === 0) {
            // Daiminkan placeholder: no discard; the same player draws from the wall.
            revealDora();
            continue;
        }
        if (typeof x === "string" && !x.startsWith("r")) {
            const { letter, pos, codes } = splitMeld(x);
            if (!/^[akf]$/.test(letter)) throw new Error(`unexpected discard entry: ${x}`);
            if (letter === "a") {
                events.push({ type: "ankan", actor: cur, consumed: codes.map(t) });
            } else if (letter === "k") {
                const addedIdx = Math.min(pos, codes.length - 1);
                events.push({
                    type: "kakan",
                    actor: cur,
                    pai: t(codes[addedIdx] as number),
                    consumed: codes.filter((_, i) => i !== addedIdx).map(t),
                });
                lastCallable = codes[addedIdx] as number; // chankan
            } else {
                events.push({ type: "kita", actor: cur, pai: "N" });
            }
            revealDora();
            continue; // rinshan / replacement draw follows for the same player
        }

        // A real discard (possibly declaring riichi).
        let code: string;
        let isReach = false;
        if (typeof x === "string") {
            if (!x.startsWith("r")) throw new Error(`unexpected discard entry: ${x}`);
            isReach = true;
            code = x.slice(1);
        } else {
            code = String(x);
        }
        const tsumogiri = code === "60";
        const paiCode = tsumogiri ? (lastDrawn[cur] as number) : Number(code);
        if (isReach) {
            events.push({ type: "reach", actor: cur });
            riichi[cur] = true;
        }
        events.push({ type: "dahai", actor: cur, pai: t(paiCode), tsumogiri });
        if (isReach) pendingReach = cur;
        lastCallable = paiCode;
        prevDiscarder = cur;

        // Who acts next: a caller whose next draw is a meld taken from us, else shimocha.
        let caller = -1;
        for (let p = 0; p < np; p++) {
            if (p === cur) continue;
            const nd = (draws[p] as Slot[])[idx[p] as number];
            if (typeof nd !== "string") continue;
            const { pos, codes } = splitMeld(nd);
            const calledIdx = nd.startsWith("c") ? 0 : Math.min(pos, codes.length - 1);
            if (
                kindOf(codes[calledIdx] as number) === kindOf(paiCode) &&
                meldSource(p, pos, np) === cur
            ) {
                caller = p;
                break;
            }
        }
        cur = caller !== -1 ? caller : (cur + 1) % np;
    }

    // Resolve the round result.
    if (!result || result.length === 0) {
        warnings.push("round has no result entry");
        events.push({ type: "end_kyoku" });
        return;
    }
    if (result[0] === "和了") {
        for (let i = 1; i + 1 < result.length; i += 2) {
            const agari = result[i + 1] as unknown as [number, number, number, ...string[]];
            // A ron on the riichi discard means the deposit was never taken.
            if (pendingReach !== null && agari[0] !== pendingReach && agari[1] === pendingReach) {
                pendingReach = null;
            }
        }
        flushReach();
        for (let i = 1; i + 1 < result.length; i += 2) {
            const deltas = result[i] as unknown as number[];
            const agari = result[i + 1] as unknown as [number, number, number, ...string[]];
            const [winner, ron] = agari;
            for (let p = 0; p < np; p++) {
                running[p] = (running[p] as number) + (deltas[p] ?? 0);
            }
            const ev: MjaiEvent = {
                type: "hora",
                actor: winner,
                target: ron,
                deltas: deltas.slice(0, np),
                scores: [...running],
            };
            const paiCode = (winner === ron ? lastDrawn[winner] : lastCallable) ?? null;
            if (paiCode !== null) ev.pai = t(paiCode);
            if (riichi[winner] && Array.isArray(uraInds) && uraInds.length > 0) {
                ev.ura_markers = uraInds.map(t);
            }
            events.push(ev);
        }
    } else {
        flushReach();
        const reason = RYUKYOKU_REASONS[result[0] as string];
        if (reason === undefined) {
            warnings.push(`unknown ryukyoku kind "${result[0]}"`);
        }
        const deltas = (Array.isArray(result[1]) ? result[1] : new Array(np).fill(0)) as number[];
        for (let p = 0; p < np; p++) {
            running[p] = (running[p] as number) + (deltas[p] ?? 0);
        }
        events.push({
            type: "ryukyoku",
            reason: reason ?? String(result[0]),
            deltas: deltas.slice(0, np),
            scores: [...running],
        });
    }
    events.push({ type: "end_kyoku" });
}
